import { computed } from '@preact/signals'
import {
	type Role,
	type Collaborator,
	GuardInput,
	ForManagingProjects,
	ProjectService
} from 'core'
import { createState } from 'shared'

import {
	addCollaboratorFromProjectDetails,
	application,
	emitSystemNotification,
	projectDetailsState,
	removeCollaboratorFromProjectDetails,
	resetSystemNotification,
	userInformationState
} from 'state'

export const DEFAULT_SELECT_VALUE = '0'

/*
	States
*/

const collaboratorEmailStateContainer = createState('')
const isDAppLoadingStateContainer = createState(false)
const collaboratorsStateContainer = createState<Collaborator[]>([])
const rolesStateContainer = createState<Role[]>([
	{
		label: 'Maintainer',
		value: 'maintainer'
	}
])
const rolSelectedStateContainer = createState(DEFAULT_SELECT_VALUE)
const formErrorsStateContainer = createState<
	Record<string, string | undefined>
>({})

export const collaboratorEmailState = collaboratorEmailStateContainer.state
export const collaboratorsState = collaboratorsStateContainer.state
export const formErrors = formErrorsStateContainer.state
export const isDAppLoading = isDAppLoadingStateContainer.state
export const rolesState = rolesStateContainer.state
export const rolSelectedState = rolSelectedStateContainer.state

/*
	UI States Management
*/

const isAddCollaboratorModalOpenStateContainer = createState(false)
const isRemoveCollaboratorModalOpenStateContainer = createState(false)
const collaboratorUsernameToRemoveStateContainer = createState('')
const isRemoveCollaboratorButtonDisabledStateContainer = createState(false)
const isAddCollaboratorSuccessStateContainer = createState(false)

export const isAddCollaboratorModalOpenState =
	isAddCollaboratorModalOpenStateContainer.state

export const isRemoveCollaboratorModalOpenState =
	isRemoveCollaboratorModalOpenStateContainer.state

export const collaboratorUsernameToRemoveState =
	collaboratorUsernameToRemoveStateContainer.state

export const isRemoveCollaboratorButtonDisabledState =
	isRemoveCollaboratorButtonDisabledStateContainer.state

export const isAddCollaboratorSuccessState =
	isAddCollaboratorSuccessStateContainer.state

export const isAddCollaboratorButtonDisabled = computed(
	() =>
		!collaboratorEmailState.value ||
		isRemoveCollaboratorButtonDisabledState.value ||
		formErrors.value.userHandler !== '' ||
		rolSelectedState.value === DEFAULT_SELECT_VALUE
)

export const setRemoveCollaboratorButtonState = (newState: boolean) => {
	isRemoveCollaboratorButtonDisabledStateContainer.updateState(() => newState)
}

/*
	Form handlers section
*/

const setFormErrors = (field: string, error?: string) => {
	formErrorsStateContainer.updateState(prevErrorState => {
		if (prevErrorState[field] === error) return prevErrorState
		return {
			...prevErrorState,
			[field]: error
		}
	})
}

collaboratorEmailStateContainer.onUpdateState(value => {
	if (!value) return

	const error = ProjectService.ValidateCollaboratorName(
		value,
		userInformationState.peek()?.email,
		projectDetailsState.peek()?.collaborators
	)

	if (error) {
		setFormErrors('userHandler', error.inputErrors?.userHandler)
	} else if (formErrors.peek().userHandler !== '') {
		setFormErrors('userHandler', '')
	}
})

collaboratorEmailStateContainer.onUpdateState(value => {
	if (!value) {
		setFormErrors('userHandler', '')
		return
	}

	const error = GuardInput.check(
		ForManagingProjects.AddCollaboratorPayload.partial(),
		{
			userHandler: value
		}
	)

	if (error) {
		setFormErrors('userHandler', error.inputErrors?.userHandler)
	} else if (formErrors.peek().userHandler !== '') {
		setFormErrors('userHandler', '')
	}
})

export const setCollaboratorEmail = (email: string) => {
	collaboratorEmailStateContainer.updateState(() => email)
}

export const setCollaboratorRole = (_: string, value: string) => {
	rolSelectedStateContainer.updateState(() => value)
}

export const resetFormAndErrors = () => {
	collaboratorEmailStateContainer.updateState(() => '')
	rolSelectedStateContainer.updateState(() => '0')
	formErrorsStateContainer.updateState(() => ({}))
}

/*
	UI State Actions Management
*/

export const handleCloseAddCollaboratorModal = () => {
	if (isRemoveCollaboratorButtonDisabledState.peek()) return

	isRemoveCollaboratorModalOpenStateContainer.updateState(() => false)
	isAddCollaboratorSuccessStateContainer.updateState(() => false)
	isAddCollaboratorModalOpenStateContainer.updateState(() => false)
	resetFormAndErrors()
	resetSystemNotification()
}

export const addCollaborator = async (payload: {
	userHandler: string
	role: string
	publicProjectId: string
}) =>
	application.peek().forManagingProjects().addCollaborator({
		userHandler: payload.userHandler,
		role: payload.role,
		publicProjectId: payload.publicProjectId
	})

export const handleAddCollaborator = async () => {
	resetSystemNotification()
	isRemoveCollaboratorButtonDisabledStateContainer.updateState(() => true)

	const { error } = await addCollaborator({
		userHandler: collaboratorEmailState.peek(),
		role: rolSelectedState.peek(),
		publicProjectId: projectDetailsState.peek()!.publicId
	})

	isRemoveCollaboratorButtonDisabledStateContainer.updateState(() => false)

	if (error) {
		emitSystemNotification({ error })

		return
	}

	addCollaboratorFromProjectDetails({
		role: rolSelectedState.peek(),
		userHandler: collaboratorEmailState.peek(),
		status: 'pending'
	})

	isAddCollaboratorSuccessStateContainer.updateState(() => true)
}

export const handleAddOtherCollaborator = () => {
	resetFormAndErrors()
	isAddCollaboratorSuccessStateContainer.updateState(() => false)
}

export const removeCollaborator = async (collaboratorUsername: string) =>
	application.peek().forManagingProjects().removeCollaborator({
		publicProjectId: projectDetailsState.peek()!.publicId,
		collaboratorUsername
	})

export const handleOpenAddCollaboratorModal = () => {
	isAddCollaboratorModalOpenStateContainer.updateState(() => true)
}

export const handleOpenRemoveCollaboratorModal = (
	collaboratorUsername: string
) => {
	collaboratorUsernameToRemoveStateContainer.updateState(
		() => collaboratorUsername
	)

	isRemoveCollaboratorModalOpenStateContainer.updateState(() => true)
}

export const handleCloseRemoveCollaboratorModal = () => {
	if (isRemoveCollaboratorButtonDisabledState.peek()) return

	isRemoveCollaboratorModalOpenStateContainer.updateState(() => false)
	collaboratorUsernameToRemoveStateContainer.updateState(() => '')
	resetSystemNotification()
}

export const handleRemoveCollaborator = async () => {
	resetSystemNotification()
	isRemoveCollaboratorButtonDisabledStateContainer.updateState(() => true)

	const { error } = await removeCollaborator(
		collaboratorUsernameToRemoveState.peek()
	)

	isRemoveCollaboratorButtonDisabledStateContainer.updateState(() => false)

	if (error) {
		emitSystemNotification({ error })
		return
	}

	removeCollaboratorFromProjectDetails(collaboratorUsernameToRemoveState.peek())
	handleCloseRemoveCollaboratorModal()
}
